import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AzureTeamsState, AzureTeamsResponse, AzureTeam, AzureTeamMember, AzureTeamSyncDifference, AzureSyncTeamsResponse, AzureTeamSyncMember } from '../../types/azureTeams';

const initialState: AzureTeamsState = {
  teams: [],
  initialTeams: [],
  loading: false,
  error: null,
  summary: null,
  importLoading: false,
  importSuccess: false,
  importError: null,
  syncDifferences: null,
  syncDifferencesLoading: false,
  syncDifferencesError: null,
  syncActionsLoading: false,
  syncActionsError: null,
};

const azureTeamsSlice = createSlice({
  name: 'azureTeams',
  initialState,
  reducers: {
    fetchAzureTeamsStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchAzureTeamsSuccess: (state, action: PayloadAction<AzureTeamsResponse>) => {
      // Ensure all teams and members have consistent selected state
      const teamsWithSelection = action.payload.teams.map(team => ({
        ...team,
        selected: team.selected || false,
        members: team.members.map(member => ({
          ...member,
          selected: member.selected || false
        }))
      }));
      
      state.loading = false;
      state.teams = teamsWithSelection;
      state.initialTeams = JSON.parse(JSON.stringify(teamsWithSelection)); // Deep copy of initial state
      state.summary = action.payload.summary;
      state.error = null;
    },
    fetchAzureTeamsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    toggleTeamSelection: (state, action: PayloadAction<string>) => {
      const teamId = action.payload;
      const team = state.teams.find(t => t.team_id === teamId);
      if (team) {
        team.selected = !team.selected;
        // When selecting a team, select all its members
        // When deselecting a team, deselect all its members
        team.members.forEach(member => {
          member.selected = team.selected;
        });
      }
    },
    toggleMemberSelection: (state, action: PayloadAction<{ teamId: string; memberId: string }>) => {
      const { teamId, memberId } = action.payload;
      const team = state.teams.find(t => t.team_id === teamId);
      if (team) {
        const member = team.members.find(m => m.id === memberId);
        if (member) {
          member.selected = !member.selected;
          
          // Automatically select the team if any member is selected
          team.selected = team.members.some(m => m.selected);
        }
      }
    },
    selectAllTeams: (state) => {
      state.teams.forEach(team => {
        team.selected = true;
      });
    },
    deselectAllTeams: (state) => {
      state.teams.forEach(team => {
        team.selected = false;
      });
    },
    selectAllMembers: (state) => {
      state.teams.forEach(team => {
        team.members.forEach(member => {
          member.selected = true;
        });
      });
    },
    deselectAllMembers: (state) => {
      state.teams.forEach(team => {
        team.members.forEach(member => {
          member.selected = false;
        });
      });
    },
    selectAll: (state) => {
      state.teams.forEach(team => {
        team.selected = true;
        team.members.forEach(member => {
          member.selected = true;
        });
      });
    },
    deselectAll: (state) => {
      state.teams.forEach(team => {
        team.selected = false;
        team.members.forEach(member => {
          member.selected = false;
        });
      });
    },
    importAzureTeamsStart: (state) => {
      state.importLoading = true;
      state.importSuccess = false;
      state.importError = null;
    },
    importAzureTeamsSuccess: (state, action: PayloadAction<any>) => {
      state.importLoading = false;
      state.importSuccess = true;
      state.importError = null;
    },
    importAzureTeamsFailure: (state, action: PayloadAction<string>) => {
      state.importLoading = false;
      state.importSuccess = false;
      state.importError = action.payload;
    },
    resetImportState: (state) => {
      state.importLoading = false;
      state.importSuccess = false;
      state.importError = null;
    },
    clearAzureTeams: (state) => {
      state.teams = [];
      state.initialTeams = [];
      state.summary = null;
      state.error = null;
      state.importError = null;
      state.importSuccess = false;
    },
    // Add Azure sync differences actions
    fetchAzureSyncDifferencesStart: (state) => {
      state.syncDifferencesLoading = true;
      state.syncDifferencesError = null;
    },
    fetchAzureSyncDifferencesSuccess: (state, action: PayloadAction<AzureSyncTeamsResponse>) => {
      state.syncDifferencesLoading = false;
      state.syncDifferences = action.payload.teams;
      state.syncDifferencesError = null;
    },
    fetchAzureSyncDifferencesFailure: (state, action: PayloadAction<string>) => {
      state.syncDifferencesLoading = false;
      state.syncDifferencesError = action.payload;
    },
    // Azure sync actions (accept/reject)
    azureSyncActionsStart: (state) => {
      state.syncActionsLoading = true;
      state.syncActionsError = null;
    },
    azureSyncActionsSuccess: (state, action: PayloadAction<{ teamId: number; memberIds: string[] }>) => {
      state.syncActionsLoading = false;
      state.syncActionsError = null;
      const { teamId, memberIds } = action.payload;

      if (!state.syncDifferences) return;

      const teamIndex = state.syncDifferences.findIndex(team => team.id === teamId);
      if (teamIndex !== -1) {
        // Filter out the members that were processed
        state.syncDifferences[teamIndex].members = state.syncDifferences[teamIndex].members.filter(
          member => !memberIds.includes(member.id)
        );

        // If no members are left in the team's sync list, remove the team entry
        if (state.syncDifferences[teamIndex].members.length === 0) {
          state.syncDifferences.splice(teamIndex, 1);
        }
      }
    },
    azureSyncActionsFailure: (state, action: PayloadAction<string>) => {
      state.syncActionsLoading = false;
      state.syncActionsError = action.payload;
    },
    clearAzureSyncDifferences: (state) => {
      state.syncDifferences = null;
      state.syncDifferencesError = null;
      state.syncActionsError = null;
    },
    removeImportedTeam: (state, action: PayloadAction<string>) => {
      const teamId = action.payload;
      // Remove the team from the list after successful import
      state.teams = state.teams.filter(team => team.team_id !== teamId);
      state.initialTeams = state.initialTeams.filter(team => team.team_id !== teamId);
    },
  },
});

export const {
  fetchAzureTeamsStart,
  fetchAzureTeamsSuccess,
  fetchAzureTeamsFailure,
  toggleTeamSelection,
  toggleMemberSelection,
  selectAllTeams,
  deselectAllTeams,
  selectAllMembers,
  deselectAllMembers,
  selectAll,
  deselectAll,
  importAzureTeamsStart,
  importAzureTeamsSuccess,
  importAzureTeamsFailure,
  resetImportState,
  clearAzureTeams,
  fetchAzureSyncDifferencesStart,
  fetchAzureSyncDifferencesSuccess,
  fetchAzureSyncDifferencesFailure,
  azureSyncActionsStart,
  azureSyncActionsSuccess,
  azureSyncActionsFailure,
  clearAzureSyncDifferences,
  removeImportedTeam,
} = azureTeamsSlice.actions;

export default azureTeamsSlice.reducer; 